import * as React from "react";
import styled from "@emotion/styled";
import { keyframes } from "@emotion/react";
import { Navigate, useNavigate } from "react-router-dom";
import {
  Button,
  Checkbox,
  FadeIn,
  H2,
  Input,
  Link,
  Text,
  LoadingSpinner,
} from "atoms";
import { constants } from "config";
import { useWalletAddress, useActiveConnector } from "features/auth";
import { useMintHelpers } from "utils/mintHelpers";
import logoSVG from "assets/images/logo.svg";
import itsTimeSVG from "assets/images/its-time-to-hug.svg";

function MintMembershipPass() {
  const [numToMint, setNumToMint] = React.useState<number | "">(1);
  const [agreed, setAgreed] = React.useState(false);
  const walletAddress = useWalletAddress();
  const connector = useActiveConnector();
  const walletProvider = connector?.provider;
  const {
    mintsUsed,
    mintsMax,
    mintPermitted,
    isLoading,
    mintNft,
    mintError,
    mintProcessing,
    mintDone,
    mintTxHash,
  } = useMintHelpers(walletAddress);
  const navigate = useNavigate();

  if (mintDone) {
    return <Navigate to="/my-nfts" />;
  }

  if (isLoading) {
    return (
      <Main>
        <LoadingSpinner />
      </Main>
    );
  }

  if (mintTxHash) {
    return (
      <Main>
        <BouncingImg
          src={logoSVG}
          alt=""
          aria-hidden="true"
          role="presentation"
        />
        <Text size="lg">Your HUGs are on their way!</Text>

        <Text size="sm">
          Stick around to see the reveal, or{" "}
          <Link
            target="_blank"
            href={`${constants.etherscanURL}/tx/${mintTxHash}`}
          >
            view your transaction on Etherscan
          </Link>
          .
        </Text>
      </Main>
    );
  }

  const reload = () => navigate(0);

  if (mintPermitted.error || mintError) {
    return (
      <Main>
        <H2>Oh No! 😭</H2>

        <Text size="lg">
          Something went wrong. Refresh the page to try again.
        </Text>

        <Button variant="gradient" onClick={reload}>
          Refresh
        </Button>
      </Main>
    );
  }

  // Constants available after load
  const oneFree =
    mintPermitted.data?.oneFree && (mintsUsed.data as number) === 0;
  const mintsMin = 1;
  const mintsRemaining = mintsMax - (mintsUsed.data as number);
  const isMultiple = !["", 1].includes(numToMint);
  let atCost = 0;
  if (!oneFree) {
    atCost = numToMint === "" ? 0 : (numToMint as number);
  } else if (isMultiple) {
    atCost = (numToMint as number) - 1;
  }

  if (!mintPermitted.data?.approved) {
    return (
      <Main>
        <H2>Oh No! 😭</H2>

        <Text size="lg">
          This wallet address isn’t on the pre-approval list.
        </Text>
      </Main>
    );
  }
  if (mintsRemaining < 1) {
    return (
      <Main>
        <H2>Oh No! 😭</H2>

        <Text size="lg">
          You have already used {mintsUsed.data as number} of {mintsMax} mints.
        </Text>
      </Main>
    );
  }

  // Setter with validation for `numToMint`
  const updateNumToMint = (n?: number) => {
    if (n === undefined) setNumToMint("");
    else if (n >= mintsMin && n <= mintsRemaining) {
      setNumToMint(n);
    }
  };

  // Form event handlers
  const onNumChange: React.ChangeEventHandler<HTMLInputElement> = ({
    target: { value },
  }) => {
    updateNumToMint(value === "" ? undefined : Number(value));
  };
  const onDecrement = () => updateNumToMint(numToMint ? numToMint - 1 : 1);
  const onIncrement = () => updateNumToMint(numToMint ? numToMint + 1 : 1);
  const onSubmit: React.FormEventHandler = (event) => {
    event.preventDefault();
    if (walletAddress && walletProvider && numToMint) {
      mintNft(walletProvider, walletAddress, numToMint);
    }
  };

  return (
    <Main>
      <HeroImg src={itsTimeSVG} alt="It's Time To Hug" />

      {mintProcessing && (
        <FadeIn>
          <Text size="lg">Check your wallet app to confirm!</Text>
        </FadeIn>
      )}

      {!mintProcessing && (
        <FadeIn>
          {mintPermitted.data?.isCurator && (
            <CuratorMessage>
              🎊 Your Curator Club NFT has been airdropped! 🎊
            </CuratorMessage>
          )}

          <FadeIn>
            <Form onSubmit={onSubmit}>
              <Label htmlFor="numToMint">How many HUGs would you like?</Label>

              <FormRow>
                <GroupedInput>
                  <NumberBtn
                    type="button"
                    onClick={onDecrement}
                    disabled={numToMint <= mintsMin}
                  >
                    -
                  </NumberBtn>
                  <NumberInput
                    type="number"
                    id="numToMint"
                    min={mintsMin}
                    max={mintsRemaining}
                    value={numToMint}
                    onChange={onNumChange}
                  />
                  <NumberBtn
                    type="button"
                    onClick={onIncrement}
                    disabled={numToMint >= mintsRemaining}
                  >
                    +
                  </NumberBtn>
                </GroupedInput>
                <Button
                  type="submit"
                  variant="gradient"
                  disabled={!agreed || !numToMint}
                >
                  Mint
                </Button>
              </FormRow>

              <SCheckbox
                label={
                  <>
                    I have read and agree to the{" "}
                    <Link to="/terms-of-service">Terms of Use</Link>
                  </>
                }
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
              />
            </Form>
          </FadeIn>

          {oneFree && (
            <Text size="sm">
              Your first Membership Pass NFT is free (plus gas)!
            </Text>
          )}
          <Text size="sm">
            {atCost ? (
              <>
                The price of {atCost} {oneFree ? "more " : ""}Membership Pass{" "}
                {atCost > 1 ? "NFTs" : "NFT"} is
                <Cost>{atCost * 0.06} Ξ</Cost>
                (plus gas).
              </>
            ) : (
              <>
                {/* Prevent layout shift if atCost is falsey */}
                &nbsp;
              </>
            )}
          </Text>
        </FadeIn>
      )}
    </Main>
  );
}

const Main = styled.main(({ theme }) => ({
  background: [
    theme.colors.bg,
    `linear-gradient(180deg, ${theme.colors.bg} 10%, ${theme.colors.green})`,
  ],
  borderRadius: theme.borderRadius.md,
  boxShadow: theme.boxShadow.dark,
  margin: theme.spacing.md,
  minWidth: "min(900px, 60%)",
  padding: "4rem",
  textAlign: "center",
}));

const HeroImg = styled.img({
  margin: "2rem 0",
  maxWidth: "100%",
});

const CuratorMessage = styled.p({}, ({ theme }) => ({
  ...theme.fonts.display,
  fontSize: theme.fontSizes.lg,
  marginBottom: theme.spacing.lg,
}));

const Form = styled.form(({ theme }) => ({ marginBottom: theme.spacing.lg }));

const FormRow = styled.div(
  {
    alignItems: "center",
    display: "flex",
    flexDirection: "column",
    gap: "1rem",
    justifyContent: "center",
    "@media (min-width: 350px)": {
      alignItems: "stretch",
      flexDirection: "row",
    },
  },
  ({ theme }) => ({ marginBottom: theme.spacing.md })
);

const Label = styled.label(
  {
    display: "block",
    width: "100%",
  },
  ({ theme }) => ({
    fontSize: theme.fontSizes.md,
    marginBottom: theme.spacing.md,
  })
);

const NumberInput = styled(Input)(
  {
    minWidth: "4rem",
    margin: 0,
    padding: "0.4rem 0",
    outlineOffset: -3,
    textAlign: "center",
    "&::-webkit-inner-spin-button,&::-webkit-outer-spin-button": {
      appearance: "none",
      margin: 0,
    },
  },
  ({ theme }) => ({
    ...theme.fonts.display,
    fontSize: theme.fontSizes.h3,
  })
);

const NumberBtn = styled.button(({ theme }) => ({
  ...theme.fonts.body,
  background: "none",
  border: "2px solid transparent",
  borderRadius: theme.borderRadius.md,
  cursor: "pointer",
  fontSize: theme.fontSizes.h3,
  minWidth: "3rem",
  padding: "0.4rem 0",
  textAlign: "center",
  transition: "transform 0.15s ease, background 0.15s ease",
  "&:active": {
    transform: "translateY(3px)",
  },
}));

const GroupedInput = styled.div(({ theme }) => ({
  background: "white",
  borderRadius: theme.borderRadius.md,
  display: "inline-flex",
  flexDirection: "row",
  gap: 5,
}));

const SCheckbox = styled(Checkbox)({}, ({ theme }) => ({
  justifyContent: "center",
  fontSize: theme.fontSizes.sm,
}));

const Cost = styled.strong({ padding: "0 0.5rem" });

const bounce = keyframes`
  from, 20%, 53%, 80%, to {
    transform: translate3d(0,0,0);
  }

  40%, 43% {
    transform: translate3d(0, -2rem, 0);
  }

  70% {
    transform: translate3d(0, -1rem, 0);
  }

  90% {
    transform: translate3d(0,-0.3rem,0);
  }
`;
const BouncingImg = styled.img({
  margin: "1rem",
  width: "min(140px, 60%)",
  "@media (prefers-reduced-motion: no-preference)": {
    animation: `${bounce} 1.2s ease infinite`,
  },
});

export { MintMembershipPass };
